import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Header } from '@/components/layout/Header';
import { StatusBadge, ChannelBadge, RiskBadge, JobTypeBadge } from '@/components/common/StatusBadge';
import { formatDate } from '@/lib/utils';
import { ScoreGauge } from '@/components/common/ScoreGauge';
import { ConversationPanel } from '@/components/common/ConversationPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApi, getErrorMessage } from '@/hooks/useApi';
import { toast } from 'sonner';
import {
  ArrowLeft,
  RefreshCw,
  Check,
  X,
  Copy,
  MessageSquare,
  FileText,
  History,
  AlertTriangle,
  CheckCircle2,
  Calendar,
  Building2,
  User,
  Edit3,
  Sparkles,
  ExternalLink
} from 'lucide-react';

export const ReviewDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const api = useApi();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [selectedTone, setSelectedTone] = useState('standard');
  const [actionLoading, setActionLoading] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/review-jobs/${jobId}`);
      setData(response.data);
      const currentDraft = response.data.drafts?.[0];
      if (currentDraft) {
        setEditedContent(currentDraft.content);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  }, [api, jobId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCopy = async () => {
    const content = editedContent || data?.drafts?.[0]?.content || '';
    try {
      await navigator.clipboard.writeText(content);
      toast.success('Texte copié dans le presse-papier');
    } catch (err) {
      // Fallback for environments without clipboard access
      const textArea = document.createElement('textarea');
      textArea.value = content;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Texte copié dans le presse-papier');
      } catch (e) {
        toast.info('Sélectionnez et copiez le texte manuellement');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await api.post(`/review-jobs/${jobId}/regenerate`, { tone: selectedTone });
      toast.success('Régénération en cours...');
      setTimeout(fetchData, 2000);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la régénération'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleSaveEdit = async () => {
    try {
      await api.post(`/review-jobs/${jobId}/edit-draft`, { content: editedContent });
      toast.success('Brouillon mis à jour');
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    }
  };

  const handleApprove = async () => {
    setActionLoading('approve');
    try {
      const response = await api.post(`/review-jobs/${jobId}/approve`);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'approbation');
    } finally {
      setActionLoading('');
    }
  };

  const handleReject = async () => {
    setActionLoading('reject');
    try {
      await api.post(`/review-jobs/${jobId}/reject`);
      toast.success('Rejeté');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionLoading('');
    }
  };

  const handleMarkPublished = async () => {
    setActionLoading('publish');
    try {
      await api.post(`/review-jobs/${jobId}/mark-published`);
      toast.success('Marqué comme publié');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setActionLoading('');
    }
  };

  const handlePublishToExtension = (platform) => {
    const content = editedContent || data?.drafts?.[0]?.content || '';
    const { job } = data || {};
    
    if (!content) {
      toast.error('Aucun brouillon à publier');
      return;
    }

    // Copy content to clipboard first
    navigator.clipboard.writeText(content).catch(() => {});

    // Create draft data for the extension
    const draftData = {
      content,
      jobId: job.id,
      type: job.job_type,
      guestName: job.guest_name,
      propertyName: job.property_name,
      channel: platform
    };

    // Store in localStorage for the extension to pick up
    localStorage.setItem(`goodtime_draft_${platform}`, JSON.stringify(draftData));

    if (platform === 'airbnb') {
      // Airbnb allows window.open
      let url;
      if (job.job_type === 'guest_review') {
        url = 'https://www.airbnb.com/hosting/reservations/completed';
      } else {
        url = 'https://www.airbnb.com/hosting/reviews';
      }

      toast.success(
        <div>
          <p className="font-medium">Texte copié !</p>
          <p className="text-sm mt-1">Airbnb va s'ouvrir. L'extension injectera le texte.</p>
        </div>,
        { duration: 5000 }
      );

      setTimeout(() => {
        window.open(url, '_blank');
      }, 500);
      
    } else if (platform === 'booking') {
      // Booking.com blocks window.open from other origins (COOP policy)
      // Show instructions instead
      toast.success(
        <div>
          <p className="font-medium">Texte copié dans le presse-papier !</p>
          <p className="text-sm mt-2">Pour publier sur Booking.com :</p>
          <ol className="text-sm mt-1 list-decimal list-inside space-y-1">
            <li>Connectez-vous à <strong>admin.booking.com</strong></li>
            <li>Allez dans la section "Avis"</li>
            <li>Trouvez l'avis du voyageur</li>
            <li>Collez le texte (Ctrl+V)</li>
          </ol>
        </div>,
        { duration: 10000 }
      );
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse-soft text-slate-400">Chargement...</div>
        </div>
      </MainLayout>
    );
  }

  const { job, ai_summary, drafts, messages, cleaning_report } = data || {};
  const currentDraft = drafts?.[0];
  const waitingCleaning = job?.status === 'waiting_cleaning_report';

  const handleRecheckCleaning = async () => {
    setActionLoading('recheck-cleaning');
    try {
      const res = await api.post(`/review-jobs/${jobId}/recheck-cleaning-report`);
      if (res.data.found) {
        toast.success('Rapport ménage trouvé, génération en cours...');
      } else {
        toast.info('Toujours en attente du retour ménage');
      }
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setActionLoading('');
    }
  };

  const handleForceGenerate = async () => {
    if (!window.confirm("Générer l'avis SANS le rapport ménage ? L'IA n'aura pas les infos de propreté.")) return;
    setActionLoading('force-generate');
    try {
      await api.post(`/review-jobs/${jobId}/force-generate`);
      toast.success('Génération lancée (sans rapport ménage)');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur'));
    } finally {
      setActionLoading('');
    }
  };

  return (
    <MainLayout>
      <Header
        title={job?.guest_name || 'Détail'}
        subtitle={job?.property_name}
      />

      <div className="p-6 space-y-6 animate-fadeIn" data-testid="review-detail">
        {/* Back button and status */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-slate-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <div className="flex items-center gap-2">
            <JobTypeBadge type={job?.job_type} />
            <ChannelBadge channel={job?.channel} />
            <StatusBadge status={job?.status} />
            <RiskBadge level={job?.risk_level} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Info card */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500 mb-1">Voyageur</p>
                    <p className="font-medium text-slate-900 flex items-center gap-1">
                      <User size={14} /> {job?.guest_name}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500 mb-1">Propriété</p>
                    <p className="font-medium text-slate-900 flex items-center gap-1">
                      <Building2 size={14} /> {job?.property_name}
                    </p>
                  </div>
                  {job?.check_in && (
                  <div>
                    <p className="text-slate-500 mb-1">Check-in</p>
                    <p className="font-medium text-slate-900 flex items-center gap-1">
                      <Calendar size={14} /> {formatDate(job?.check_in)}
                    </p>
                  </div>
                  )}
                  {job?.check_out && (
                  <div>
                    <p className="text-slate-500 mb-1">Check-out</p>
                    <p className="font-medium text-slate-900 flex items-center gap-1">
                      <Calendar size={14} /> {formatDate(job?.check_out)}
                    </p>
                  </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Waiting for cleaning report banner */}
            {waitingCleaning && (
              <Card className="bg-amber-50 border-amber-300">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle size={18} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-amber-900">En attente du retour ménage</p>
                      <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                        L'IA ne peut pas rédiger l'avis voyageur tant que le prestataire ménage
                        n'a pas filé son rapport de fin de séjour. Le rapport sera détecté
                        automatiquement dès qu'il sera saisi côté Goodtime Master.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button
                          size="sm"
                          onClick={handleRecheckCleaning}
                          disabled={actionLoading === 'recheck-cleaning'}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <RefreshCw className={`mr-2 h-4 w-4 ${actionLoading === 'recheck-cleaning' ? 'animate-spin' : ''}`} />
                          Vérifier maintenant
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={handleForceGenerate}
                          disabled={actionLoading === 'force-generate'}
                          className="text-amber-700 border-amber-300"
                        >
                          <Sparkles className="mr-2 h-4 w-4" />
                          Générer sans rapport
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cleaning report (when received) */}
            {cleaning_report && (
              <Card className="bg-emerald-50 border-emerald-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-emerald-800 flex items-center gap-2">
                    <CheckCircle2 size={16} />
                    Rapport ménage de fin de séjour
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-sm space-y-1.5">
                  {cleaning_report.cleaner_name && (
                    <p><span className="text-emerald-700 font-medium">Prestataire :</span> {cleaning_report.cleaner_name}</p>
                  )}
                  {cleaning_report.cleaning_date && (
                    <p><span className="text-emerald-700 font-medium">Date :</span> {cleaning_report.cleaning_date}</p>
                  )}
                  {cleaning_report.cleanliness_rating != null && (
                    <p><span className="text-emerald-700 font-medium">Note propreté :</span> {cleaning_report.cleanliness_rating}</p>
                  )}
                  {cleaning_report.cleanliness_label && (
                    <p><span className="text-emerald-700 font-medium">État :</span> {cleaning_report.cleanliness_label}</p>
                  )}
                  {cleaning_report.damages_reported && (
                    <p><span className="text-emerald-700 font-medium">Dégâts :</span> {cleaning_report.damages_reported}</p>
                  )}
                  {cleaning_report.missing_items && (
                    <p><span className="text-emerald-700 font-medium">Manquants :</span> {cleaning_report.missing_items}</p>
                  )}
                  {cleaning_report.notes && (
                    <p className="italic mt-2 pt-2 border-t border-emerald-200">{cleaning_report.notes}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Guest comment (for host replies) */}
            {job?.guest_comment && (
              <Card className="bg-amber-50 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-amber-800 flex items-center gap-2">
                    <MessageSquare size={16} />
                    Commentaire du voyageur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-amber-900 italic">"{job.guest_comment}"</p>
                </CardContent>
              </Card>
            )}

            {/* Draft section */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <FileText size={18} className="text-[#FF5A6E]" />
                  Brouillon
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Select value={selectedTone} onValueChange={setSelectedTone}>
                    <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="tone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="chaleureux">Chaleureux</SelectItem>
                      <SelectItem value="ferme_diplomatique">Ferme diplomatique</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleRegenerate}
                    disabled={regenerating}
                    className="text-[#2E86DE] border-[#2E86DE]"
                    data-testid="regenerate-btn"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${regenerating ? 'animate-spin' : ''}`} />
                    Régénérer
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-3">
                    <Textarea
                      value={editedContent}
                      onChange={(e) => setEditedContent(e.target.value)}
                      className="min-h-[150px]"
                      data-testid="edit-textarea"
                    />
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" onClick={() => setIsEditing(false)}>
                        Annuler
                      </Button>
                      <Button
                        onClick={handleSaveEdit}
                        className="bg-[#FF5A6E] hover:bg-[#FF3B55]"
                        data-testid="save-edit-btn"
                      >
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="bg-slate-50 rounded-lg p-4 min-h-[100px]">
                      <p className="text-slate-800 whitespace-pre-wrap">
                        {currentDraft?.content || 'Aucun brouillon généré'}
                      </p>
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <span className="text-xs text-slate-400">
                        Version {currentDraft?.version || 1} • {currentDraft?.tone || 'standard'}
                      </span>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setIsEditing(true)}
                          data-testid="edit-btn"
                        >
                          <Edit3 className="mr-2 h-4 w-4" />
                          Modifier
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleCopy}
                          data-testid="copy-btn"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copier
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardContent className="p-4">
                <div className="flex flex-wrap gap-3">
                  {['draft_ready', 'needs_human_review'].includes(job?.status) && (
                    <>
                      <Button
                        onClick={handleApprove}
                        disabled={actionLoading === 'approve'}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="approve-btn"
                      >
                        <Check className="mr-2 h-4 w-4" />
                        {actionLoading === 'approve' ? 'En cours...' : 'Approuver'}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={handleReject}
                        disabled={actionLoading === 'reject'}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        data-testid="reject-btn"
                      >
                        <X className="mr-2 h-4 w-4" />
                        Rejeter
                      </Button>
                    </>
                  )}

                  {job?.status === 'manual_publish_required' && (
                    <>
                      {job?.channel === 'airbnb' && (
                        <Button
                          onClick={() => handlePublishToExtension('airbnb')}
                          className="bg-[#FF5A5F] hover:bg-[#E04E52] text-white"
                          data-testid="publish-airbnb-btn"
                        >
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Publier sur Airbnb
                        </Button>
                      )}
                      {job?.channel === 'booking' && (
                        <Button
                          onClick={() => handlePublishToExtension('booking')}
                          className="bg-[#0071C2] hover:bg-[#005999] text-white"
                          data-testid="publish-booking-btn"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copier pour Booking
                        </Button>
                      )}
                      {!['airbnb', 'booking'].includes(job?.channel) && (
                        <Button
                          onClick={handleCopy}
                          className="bg-[#2E86DE] hover:bg-[#1A6CB8] text-white"
                          data-testid="copy-publish-btn"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copier pour publier
                        </Button>
                      )}
                      <Button
                        onClick={handleMarkPublished}
                        disabled={actionLoading === 'publish'}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="mark-published-btn"
                      >
                        <CheckCircle2 className="mr-2 h-4 w-4" />
                        Marquer comme publié
                      </Button>
                    </>
                  )}

                  {job?.status === 'approved' && (
                    <div className="flex items-center gap-2 text-amber-600">
                      <AlertTriangle size={16} />
                      <span className="text-sm">Publication automatique non disponible pour ce canal</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* AI Summary */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Sparkles size={18} className="text-violet-500" />
                  Analyse IA
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-center">
                  <ScoreGauge score={job?.confidence_score || 0.5} label="Score de confiance" />
                </div>

                {ai_summary && (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Communication</span>
                      <span className="font-medium capitalize">{ai_summary.communication_quality}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Ton du voyageur</span>
                      <span className="font-medium capitalize">{ai_summary.guest_tone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Sentiment</span>
                      <span className="font-medium capitalize">{ai_summary.stay_sentiment?.replace('_', ' ')}</span>
                    </div>
                    {ai_summary.issue_detected && (
                      <div className="p-2 bg-rose-50 rounded-lg border border-rose-100">
                        <p className="text-rose-700 text-xs font-medium">Problème détecté</p>
                        <p className="text-rose-600 text-xs mt-1">{ai_summary.issue_description}</p>
                      </div>
                    )}
                    {ai_summary.internal_summary && (
                      <div className="pt-3 border-t border-slate-100">
                        <p className="text-slate-500 text-xs mb-1">Résumé interne</p>
                        <p className="text-slate-700">{ai_summary.internal_summary}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Conversation */}
            <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
              <CardHeader>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <MessageSquare size={18} className="text-[#2E86DE]" />
                  Conversation ({messages?.length || 0})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 max-h-[400px] overflow-y-auto">
                <ConversationPanel messages={messages || []} />
              </CardContent>
            </Card>

            {/* Draft history */}
            {Array.isArray(drafts) && drafts.length > 1 && (
              <Card className="bg-white border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                <CardHeader>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <History size={18} className="text-slate-500" />
                    Historique ({drafts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {drafts.slice(1).map((draft) => (
                    <div
                      key={draft.id}
                      className="p-2 bg-slate-50 rounded-lg text-sm"
                    >
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Version {draft.version}</span>
                        <span>{draft.tone}</span>
                      </div>
                      <p className="text-slate-600 line-clamp-2">{draft.content}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ReviewDetailPage;
